import { cn } from "@/lib/utils";

interface QuickStatCardProps {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  sentiment?: "positive" | "neutral" | "negative";
  className?: string;
}

const sentimentStyles = {
  positive: {
    border: "border-green-500/30 hover:border-green-500/50",
    bg: "bg-gradient-to-br from-green-500/10 to-green-500/5",
    icon: "text-green-600",
    value: "text-green-700 dark:text-green-400",
  },
  neutral: {
    border: "border-amber-500/30 hover:border-amber-500/50",
    bg: "bg-gradient-to-br from-amber-500/10 to-amber-500/5",
    icon: "text-amber-600",
    value: "text-amber-700 dark:text-amber-400",
  },
  negative: {
    border: "border-red-500/30 hover:border-red-500/50",
    bg: "bg-gradient-to-br from-red-500/10 to-red-500/5",
    icon: "text-red-600",
    value: "text-red-700 dark:text-red-400",
  },
};

export const QuickStatCard = ({
  icon,
  value,
  label,
  sentiment = "neutral",
  className,
}: QuickStatCardProps) => {
  const styles = sentimentStyles[sentiment];

  return (
    <div
      className={cn(
        "group relative p-4 rounded-xl border-2 transition-all duration-300",
        "hover:shadow-lg hover:scale-[1.02]",
        styles.border,
        styles.bg,
        className
      )}
    >
      <div className="flex items-center gap-3">
        {/* Icon */}
        <div
          className={cn(
            "flex-shrink-0 p-2 rounded-lg bg-background/50 transition-transform group-hover:scale-110",
            styles.icon
          )}
        >
          {icon}
        </div>

        {/* Value and Label */}
        <div className="min-w-0 flex-1">
          <div className={cn("text-xl font-bold truncate", styles.value)}>
            {value}
          </div>
          <div className="text-xs text-muted-foreground truncate">{label}</div>
        </div>
      </div>
    </div>
  );
};

// Helper function to determine sentiment from various inputs
export const getSentimentFromImpact = (impact?: string): "positive" | "neutral" | "negative" => {
  if (!impact) return "neutral";
  const lower = impact.toLowerCase();
  
  if (lower.includes("excellent") || lower.includes("positive") || lower.includes("good") || lower.includes("low crime") || lower.includes("quiet")) {
    return "positive";
  }
  if (lower.includes("negative") || lower.includes("poor") || lower.includes("high crime") || lower.includes("noisy") || lower.includes("very noisy")) {
    return "negative";
  }
  return "neutral";
};

// Helper to format numbers conversationally
export const formatStatValue = (value: number | string | undefined, type: "count" | "distance" | "time" | "currency"): string => {
  if (value === undefined || value === null) return "N/A";
  
  if (typeof value === "string") return value;
  
  switch (type) {
    case "count":
      if (value === 0) return "None";
      if (value <= 5) return String(value);
      if (value <= 20) return `${value}+`;
      if (value <= 50) return "Many";
      return "Heaps";
    case "distance":
      if (value < 1000) return `${value}m`;
      return `${(value / 1000).toFixed(1)}km`;
    case "time":
      if (value < 60) return `${value}s`;
      return `${Math.round(value / 60)}min`;
    case "currency":
      if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
      return `$${value}`;
    default:
      return String(value);
  }
};

export default QuickStatCard;


